import React, {useState} from 'react';
import {
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import api from '../../api';
import QuotaBlockedNotice from '../../components/subscription/QuotaBlockedNotice';
import {Link, useNavigate} from 'react-router-dom';
import {NumericFormat} from "react-number-format";
import TextEditor from "../../components/TextEditor";
import DidNotLoad from "../../components/DidNotLoad";
import { parseFormattedMoneyToCents } from '../../helpers/formatters';
import {
    PRODUCT_DIGITAL,
    PRODUCT_MTO,
    PRODUCT_STOCK,
    normalizeProductKind,
    productKindLabel,
} from '../../helpers/orderStages';

function RegisterProductPage() {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [basePrice, setBasePrice] = useState('');
    const [costPrice, setCostPrice] = useState('');
    const [basePriceFloat, setBasePriceFloat] = useState(undefined);
    const [costPriceFloat, setCostPriceFloat] = useState(undefined);
    const [customizable, setCustomizable] = useState(false);
    const [assemblyRequired, setAssemblyRequired] = useState(false);
    const [productKind, setProductKind] = useState(PRODUCT_STOCK);
    const [stockQuantity, setStockQuantity] = useState('');
    const [totalSalesQuantity, setTotalSalesQuantity] = useState('');
    const [measures, setMeasures] = useState({
        width: '',
        height: '',
        length: '',
    });
    const [availability, setAvailability] = useState({
        lead_time_for_order: 1,
    });
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        name: null,
        description: null,
        sale_price: null,
        cost_price: null,
        is_customizable: null,
        assembly_required: null,
        warranty_details: null,
        dimensional_tolerance: null,
        measures: null,
        has_errors: false
    });
    const [hasResponse, setHasResponse] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            // Verifica o tipo de arquivo (JPG/PNG) e tamanho (máximo 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setFileError("O arquivo não pode ser maior que 2MB.");
                return;
            }
            if (!["image/jpeg", "image/png"].includes(file.type)) {
                setFileError("Somente arquivos JPG ou PNG são permitidos.");
                return;
            }

            setFileError(""); // Limpa erro

            // Usar FileReader para ler o arquivo como base64
            const reader = new FileReader();

            reader.onloadend = () => {
                setFile({
                    name: file.name,
                    size: file.size,  // Armazenar o tamanho do arquivo
                    base64: reader.result
                });
            };

            reader.readAsDataURL(file); // Lê a imagem e converte para base64
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false
        if (name === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                name: 'O nome do produto é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (basePrice === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                base_price: 'O preço de venda é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading

        let parsedMeasures = {};
        if (measures.height !== "") {
            parsedMeasures.height = parseInt(measures.height*100);
        }
        if (measures.width !== "") {
            parsedMeasures.width = parseInt(measures.width*100);
        }
        if (measures.length !== "") {
            parsedMeasures.length = parseInt(measures.length*100);
        }

        const kind = normalizeProductKind(productKind);
        let costCents = parseFormattedMoneyToCents(costPriceFloat, costPrice);
        // Digital sem custo: 1 centavo evita o gRPC omitir cost_price=0 e a API reclamar de campo obrigatório.
        if (kind === PRODUCT_DIGITAL && costCents <= 0) {
            costCents = 1;
        }
        const newProduct = {
            name: name,
            description: description,
            base_price: parseFormattedMoneyToCents(basePriceFloat, basePrice),
            cost_price: costCents,
            is_customizable: customizable,
            assembly_required: assemblyRequired,
            kind,
            stock_quantity: kind === PRODUCT_DIGITAL ? 0 : Math.max(0, parseInt(String(stockQuantity), 10) || 0),
            total_sales_quantity: Math.max(0, parseInt(String(totalSalesQuantity), 10) || 0),
            measures: parsedMeasures,
            availability: {
                lead_time_for_order: Math.max(
                    1,
                    parseInt(String(availability.lead_time_for_order), 10) || 1,
                ),
            },
        };
        if (file) {
            newProduct.image = file.base64;
        }

        api.post('products', newProduct)
            .then((response) => {
                setHasResponse(true);
                console.log('Produto criado com sucesso:', response);
                navigate('/admin/products');  // Redireciona de volta para a lista de impressoras
            })
            .catch((error) => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    const handleRemoveFile = () => {
        setFile(null); // Reseta o estado do arquivo
        setFileError(''); // Opcional: Reseta também o erro, caso exista
    };

    const handleCustomizableChange = (event) => {
        setCustomizable(event.target.checked);
    };

    const handleAssemblyRequiredChange = (event) => {
        setAssemblyRequired(event.target.checked);
    };

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/products')} to={'/admin/products'}>
                    Produtos
                </Link>
                <Typography color="text.primary">Adicionando produto</Typography>
            </Breadcrumbs>
            <br/>
            <Typography variant="h4" gutterBottom>Adicionar Novo Produto</Typography>
            <QuotaBlockedNotice quotaKey="products" backTo="/admin/products" />
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Título do produto (obrigatório)"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>

                    <TextEditor
                        description={description}
                        setDescription={setDescription}
                        error={resultErrors.description}
                    />
                    <Grid item xs={12} sm={12} mt={2}></Grid>
                    <Grid item xs={12} sm={12} mt={2}>
                        <Typography variant="h6">Imagem principal</Typography>
                        <input
                            type="file"
                            accept=".jpg, .jpeg, .png"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            id="file-upload"
                        />
                        <label htmlFor="file-upload">
                            <Button variant="contained" component="span" color="primary">
                                Selecionar Imagem
                            </Button>
                        </label>

                        {fileError && <Typography color="error">{fileError}</Typography>}
                        {file && (
                            <>
                                <Typography mt={2}>
                                    Arquivo selecionado: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)&nbsp;&nbsp;
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleRemoveFile}
                                        sx={{ mt: 1 }}
                                    >
                                        Remover Arquivo
                                    </Button>
                                </Typography>

                            </>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12} mt={2}>
                        <hr/>
                        <Typography variant="h6">Precificação</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Custo de produção"
                            fullWidth
                            customInput={TextField}
                            value={costPrice}
                            onValueChange={(values) => {
                                setCostPrice(values.value);
                                setCostPriceFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.cost_price && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.cost_price}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Preço de venda (obrigatório)"
                            fullWidth
                            customInput={TextField}
                            value={basePrice}
                            onValueChange={(values) => {
                                setBasePrice(values.value);
                                setBasePriceFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.base_price && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.base_price}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Tipo do produto"
                            fullWidth
                            value={productKind}
                            onChange={(e) => {
                                const nextKind = e.target.value;
                                setProductKind(nextKind);
                                if (nextKind === PRODUCT_DIGITAL) {
                                    const currentCents = parseFormattedMoneyToCents(costPriceFloat, costPrice);
                                    if (currentCents <= 0) {
                                        setCostPrice('0.01');
                                        setCostPriceFloat(0.01);
                                    }
                                }
                            }}
                            helperText="Define estoque, produção e canais em que pode ser vendido"
                        >
                            <MenuItem value={PRODUCT_STOCK}>{productKindLabel(PRODUCT_STOCK)}</MenuItem>
                            <MenuItem value={PRODUCT_MTO}>{productKindLabel(PRODUCT_MTO)}</MenuItem>
                            <MenuItem value={PRODUCT_DIGITAL}>{productKindLabel(PRODUCT_DIGITAL)}</MenuItem>
                        </TextField>
                    </Grid>
                    {productKind !== PRODUCT_DIGITAL ? (
                        <>
                    <Grid item xs={12} sm={12} mt={2}>
                        <hr/>
                        <Typography variant="h6">Medidas</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Largura (cm)"
                            fullWidth
                            type="number"
                            value={measures.width}
                            onChange={(e) => setMeasures((prev) => ({ ...prev, width: e.target.value }))}
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Altura (cm)"
                            fullWidth
                            type="number"
                            value={measures.height}
                            onChange={(e) => setMeasures((prev) => ({ ...prev, height: e.target.value }))}
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Profundidade (cm)"
                            fullWidth
                            type="number"
                            value={measures.length}
                            onChange={(e) => setMeasures((prev) => ({ ...prev, length: e.target.value }))}
                            inputMode="numeric"
                        />
                    </Grid>
                        </>
                    ) : null}
                    <Grid item xs={12} sm={12} mt={2}>
                        <hr/>
                        <Typography variant="h6">Outras informações</Typography>
                    </Grid>
                    {productKind === PRODUCT_STOCK ? (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Estoque"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            value={stockQuantity}
                            onChange={(e) => {
                                const v = e.target.value;
                                setStockQuantity(v === '' ? '' : Math.max(0, parseInt(v, 10) || 0));
                            }}
                            inputMode="numeric"
                        />
                    </Grid>
                    ) : null}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Quantidade total vendida"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            value={totalSalesQuantity}
                            onChange={(e) => {
                                const v = e.target.value;
                                setTotalSalesQuantity(v === '' ? '' : Math.max(0, parseInt(v, 10) || 0));
                            }}
                            helperText="Pode ser informada manualmente para histórico de vendas."
                            inputMode="numeric"
                        />
                    </Grid>
                    {productKind === PRODUCT_MTO ? (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Dias necessários para produção"
                            fullWidth
                            type="number"
                            value={availability.lead_time_for_order}
                            onChange={(e) => {
                                const v = e.target.value;
                                setAvailability((prev) => ({
                                    ...prev,
                                    lead_time_for_order: v === '' ? '' : Math.max(1, parseInt(v, 10) || 1),
                                }));
                            }}
                            helperText="Se vazio, será considerado 1 dia."
                            inputMode="numeric"
                        />
                    </Grid>
                    ) : null}
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={customizable}
                                    onChange={handleCustomizableChange}
                                    color="primary"
                                />
                            }
                            label="Produto customizável"
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={assemblyRequired}
                                    onChange={handleAssemblyRequiredChange}
                                    color="primary"
                                />
                            }
                            label="Requer montagem"
                        />
                    </Grid>
                </Grid>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', marginTop: 2, marginBottom: 20}}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting} // Desativa o botão durante o envio
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit"/>}
                        fullWidth
                    >
                        {isSubmitting ? 'Cadastrando...' : 'Cadastrar Produto'}
                    </Button>
                </Box>
            </form>
        </div>

    );
}

export default RegisterProductPage;
